import { useId, type ComponentProps, type ReactNode } from "react";
import { AlertCircle, Info } from "lucide-react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { NativeSelect } from "@/components/ui/native-select";
import { cn } from "@/lib/utils";

export function PageHeading({ title, description, actions }: {
  title: string; description: string; actions?: ReactNode;
}) {
  return (
    <div className="lab-page-heading flex flex-wrap items-start justify-between gap-4">
      <div className="min-w-0 space-y-1.5">
        <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
        <p className="max-w-2xl text-sm text-muted-foreground">{description}</p>
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

export function Panel({ title, description, action, children, className, contentClassName, ...props }: {
  title: ReactNode; description?: ReactNode; action?: ReactNode; children: ReactNode;
  contentClassName?: string;
} & Omit<ComponentProps<typeof Card>, "title">) {
  return (
    <Card className={cn("lab-panel min-w-0 gap-5 py-5", className)} {...props}>
      <CardHeader className="lab-panel-header flex flex-wrap items-start justify-between gap-3 px-4 sm:px-5">
        <div className="min-w-0 space-y-1">
          <h2 className="text-base font-semibold tracking-tight [overflow-wrap:anywhere]">{title}</h2>
          {description && <p className="text-sm text-muted-foreground">{description}</p>}
        </div>
        {action}
      </CardHeader>
      <CardContent className={cn("lab-panel-body space-y-4 px-4 sm:px-5", contentClassName)}>{children}</CardContent>
    </Card>
  );
}

export function Notice({ title, children, error = false }: { title?: string; children: ReactNode; error?: boolean }) {
  const Icon = error ? AlertCircle : Info;
  return (
    <div role={error ? "alert" : undefined} className={cn(
      "flex items-start gap-3 rounded-xl border bg-muted/40 p-4 text-sm",
      error && "border-destructive/25 bg-destructive/5 text-destructive",
    )}>
      <Icon aria-hidden="true" className="mt-0.5 size-4 shrink-0" />
      <div className="min-w-0 space-y-1 [overflow-wrap:anywhere]">
        {title && <p className="font-medium">{title}</p>}
        <div className={cn("text-muted-foreground", error && "text-destructive")}>{children}</div>
      </div>
    </div>
  );
}

export function InputField({ label, hint, className, ...props }: ComponentProps<typeof Input> & {
  label: string; hint?: string;
}) {
  const generated = useId();
  const id = props.id ?? generated;
  return (
    <div className={cn("min-w-0 space-y-2", className)}>
      <Label htmlFor={id} className="leading-snug">{label}</Label>
      <Input {...props} id={id} aria-describedby={[props["aria-describedby"], hint ? `${id}-hint` : null].filter(Boolean).join(" ") || undefined} />
      {hint && <p id={`${id}-hint`} className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

export function SelectField({ label, className, children, ...props }: ComponentProps<typeof NativeSelect> & { label: string }) {
  const generated = useId();
  const id = props.id ?? generated;
  return (
    <div className={cn("min-w-0 space-y-2", className)}>
      <Label htmlFor={id} className="text-xs leading-snug text-muted-foreground">{label}</Label>
      <NativeSelect {...props} id={id}>{children}</NativeSelect>
    </div>
  );
}

export function Metrics({ items }: { items: { label: string; value: ReactNode; unit?: string; note: string }[] }) {
  return (
    <div className="metrics grid grid-cols-1 divide-y rounded-xl border bg-muted/20 sm:grid-cols-3 sm:divide-x sm:divide-y-0">
      {items.map(item => (
        <div key={item.label} className="grid min-w-0 grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-1 px-3 py-3 sm:block sm:space-y-1 sm:px-4">
          <span className="text-xs text-muted-foreground">{item.label}</span>
          <div className="col-start-2 row-span-2 row-start-1 flex flex-wrap items-baseline gap-1.5 sm:row-auto">
            <strong className="text-2xl font-semibold tracking-tight tabular-nums">{item.value}</strong>
            {item.unit && <span className="text-xs text-muted-foreground">{item.unit}</span>}
          </div>
          <p className="text-xs text-muted-foreground">{item.note}</p>
        </div>
      ))}
    </div>
  );
}
